import { useState, useRef } from 'react';
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Slider } from "@/components/ui/slider";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Download, Save, Type, Palette, Move, Settings } from "lucide-react";
import { toast } from "sonner";
import html2canvas from 'html2canvas';

interface SimpleInvitationEditorProps {
  backgroundImageUrl: string;
  qrCodeData: string;
  eventData: {
    name: string;
    type: string;
    description?: string;
    date?: string;
    time?: string;
    location?: string;
    hostName?: string;
    deadline?: string;
  };
  onSave: (finalImageUrl: string) => void;
}

export const SimpleInvitationEditor: React.FC<SimpleInvitationEditorProps> = ({
  backgroundImageUrl,
  qrCodeData,
  eventData,
  onSave
}) => {
  const previewRef = useRef<HTMLDivElement>(null);
  const [editableData, setEditableData] = useState({
    name: eventData.name || '',
    hostName: eventData.hostName || '',
    description: eventData.description || '',
    date: eventData.date || '',
    time: eventData.time || '',
    location: eventData.location || ''
  });

  const [styleOptions, setStyleOptions] = useState({
    titleFontSize: 14,
    titleColor: '#6366f1',
    titleFont: 'font-bold',
    titleAlign: 'text-center',
    hostFontSize: 12,
    hostColor: '#22c55e',
    contentFontSize: 12,
    contentColor: '#1f2937',
    contentBgOpacity: 0,
    qrPosition: 'bottom-right',
    qrSize: 96, // QR code size in pixels
    qrX: 0, // QR code X position (0-100%)
    qrY: 0, // QR code Y position (0-100%)
    textShadow: true
  });

  const updateField = (field: string, value: string) => {
    setEditableData(prev => ({ ...prev, [field]: value }));
  };

  const updateStyle = (field: string, value: any) => {
    setStyleOptions(prev => ({ ...prev, [field]: value }));
  };

  const colorPresets = [
    { name: 'White', value: '#ffffff' },
    { name: 'Primary', value: 'hsl(251 91% 60%)' },
    { name: 'Accent', value: 'hsl(142 76% 36%)' },
    { name: 'Warning', value: 'hsl(38 92% 50%)' },
    { name: 'Dark', value: '#1f2937' },
    { name: 'Light Gray', value: '#9ca3af' }
  ];

  const fontOptions = [
    { name: 'Bold', value: 'font-bold' },
    { name: 'Semibold', value: 'font-semibold' },
    { name: 'Medium', value: 'font-medium' },
    { name: 'Normal', value: 'font-normal' }
  ];

  const qrPositions = [
    { name: 'Bottom Right', value: 'bottom-right' },
    { name: 'Bottom Left', value: 'bottom-left' },
    { name: 'Top Right', value: 'top-right' },
    { name: 'Top Left', value: 'top-left' }
  ];

  const exportImage = async () => {
    if (!previewRef.current) return;

    try {
      const canvas = await html2canvas(previewRef.current, {
        backgroundColor: '#ffffff',
        scale: 2,
        useCORS: true,
        allowTaint: true
      });
      
      const dataURL = canvas.toDataURL('image/png', 1.0);
      onSave(dataURL);
      toast.success('Invitation saved successfully!');
    } catch (error) {
      console.error('Error exporting image:', error);
      toast.error('Failed to save invitation');
    }
  };

  const downloadImage = async () => {
    if (!previewRef.current) return;

    try {
      const canvas = await html2canvas(previewRef.current, {
        backgroundColor: '#ffffff',
        scale: 2,
        useCORS: true,
        allowTaint: true
      });
      
      const dataURL = canvas.toDataURL('image/png', 1.0);
      const link = document.createElement('a');
      link.href = dataURL;
      link.download = `${editableData.name.replace(/\s+/g, '_')}_invitation.png`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      toast.success('Invitation downloaded!');
    } catch (error) {
      console.error('Error downloading image:', error);
      toast.error('Failed to download invitation');
    }
  };

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Type className="h-5 w-5" />
            Invitation Editor
            <Badge variant="default" className="ml-auto">Live Preview</Badge>
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Editor Panel */}
            <div className="space-y-4">
              <Tabs defaultValue="content" className="w-full">
                <TabsList className="grid w-full grid-cols-3">
                  <TabsTrigger value="content" className="flex items-center gap-2">
                    <Type className="h-4 w-4" />
                    Content
                  </TabsTrigger>
                  <TabsTrigger value="style" className="flex items-center gap-2">
                    <Palette className="h-4 w-4" />
                    Style
                  </TabsTrigger>
                  <TabsTrigger value="layout" className="flex items-center gap-2">
                    <Settings className="h-4 w-4" />
                    Layout
                  </TabsTrigger>
                </TabsList>

                <TabsContent value="content" className="space-y-4">
                  <div className="p-4 border rounded-lg bg-muted/20">
                    <h3 className="font-semibold mb-4">Edit Event Details</h3>
                    
                    <div className="space-y-4">
                      <div>
                        <Label htmlFor="event-name">Event Name</Label>
                        <Input
                          id="event-name"
                          value={editableData.name}
                          onChange={(e) => updateField('name', e.target.value)}
                          placeholder="Enter event name..."
                        />
                      </div>

                      <div>
                        <Label htmlFor="host-name">Host Name</Label>
                        <Input
                          id="host-name"
                          value={editableData.hostName}
                          onChange={(e) => updateField('hostName', e.target.value)}
                          placeholder="Enter host name..."
                        />
                      </div>

                      <div>
                        <Label htmlFor="description">Description</Label>
                        <Textarea
                          id="description"
                          value={editableData.description}
                          onChange={(e) => updateField('description', e.target.value)}
                          placeholder="Enter event description..."
                          rows={3}
                        />
                      </div>

                      <div>
                        <Label htmlFor="date">Date</Label>
                        <Input
                          id="date"
                          value={editableData.date}
                          onChange={(e) => updateField('date', e.target.value)}
                          placeholder="Enter event date..."
                        />
                      </div>

                      <div>
                        <Label htmlFor="time">Time</Label>
                        <Input
                          id="time"
                          value={editableData.time}
                          onChange={(e) => updateField('time', e.target.value)}
                          placeholder="Enter event time..."
                        />
                      </div>

                      <div>
                        <Label htmlFor="location">Location</Label>
                        <Input
                          id="location"
                          value={editableData.location}
                          onChange={(e) => updateField('location', e.target.value)}
                          placeholder="Enter event location..."
                        />
                      </div>
                    </div>
                  </div>
                </TabsContent>

                <TabsContent value="style" className="space-y-4">
                  <div className="p-4 border rounded-lg bg-muted/20">
                    <h3 className="font-semibold mb-4">Typography & Colors</h3>
                    
                    <div className="space-y-6">
                      <div>
                        <Label className="text-sm font-medium">Title Style</Label>
                        <div className="grid grid-cols-2 gap-3 mt-2">
                          <div>
                            <Label className="text-xs">Font Size</Label>
                            <Slider
                              value={[styleOptions.titleFontSize]}
                              onValueChange={(value) => updateStyle('titleFontSize', value[0])}
                              max={60}
                              min={16}
                              step={2}
                              className="mt-1"
                            />
                            <span className="text-xs text-muted-foreground">{styleOptions.titleFontSize}px</span>
                          </div>
                          <div>
                            <Label className="text-xs">Font Weight</Label>
                            <Select value={styleOptions.titleFont} onValueChange={(value) => updateStyle('titleFont', value)}>
                              <SelectTrigger className="mt-1">
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                {fontOptions.map(font => (
                                  <SelectItem key={font.value} value={font.value}>{font.name}</SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </div>
                        </div>
                        <div className="mt-3">
                          <Label className="text-xs">Color</Label>
                          <div className="flex gap-2 mt-1">
                            {colorPresets.map(color => (
                              <button
                                key={color.value}
                                className="w-6 h-6 rounded border-2 border-border hover:scale-110 transition-transform"
                                style={{ backgroundColor: color.value }}
                                onClick={() => updateStyle('titleColor', color.value)}
                                title={color.name}
                              />
                            ))}
                          </div>
                        </div>
                      </div>

                      <div>
                        <Label className="text-sm font-medium">Host Name Style</Label>
                        <div className="grid grid-cols-2 gap-3 mt-2">
                          <div>
                            <Label className="text-xs">Font Size</Label>
                            <Slider
                              value={[styleOptions.hostFontSize]}
                              onValueChange={(value) => updateStyle('hostFontSize', value[0])}
                              max={32}
                              min={12}
                              step={1}
                              className="mt-1"
                            />
                            <span className="text-xs text-muted-foreground">{styleOptions.hostFontSize}px</span>
                          </div>
                          <div>
                            <Label className="text-xs">Color</Label>
                            <div className="flex gap-1 mt-1">
                              {colorPresets.slice(0, 4).map(color => (
                                <button
                                  key={color.value}
                                  className="w-5 h-5 rounded border border-border hover:scale-110 transition-transform"
                                  style={{ backgroundColor: color.value }}
                                  onClick={() => updateStyle('hostColor', color.value)}
                                  title={color.name}
                                />
                              ))}
                            </div>
                          </div>
                        </div>
                      </div>

                      <div>
                        <Label className="text-sm font-medium">Content Box</Label>
                        <div className="grid grid-cols-2 gap-3 mt-2">
                          <div>
                            <Label className="text-xs">Background Opacity</Label>
                            <Slider
                              value={[styleOptions.contentBgOpacity]}
                              onValueChange={(value) => updateStyle('contentBgOpacity', value[0])}
                              max={100}
                              min={0}
                              step={5}
                              className="mt-1"
                            />
                            <span className="text-xs text-muted-foreground">{styleOptions.contentBgOpacity}%</span>
                          </div>
                          <div>
                            <Label className="text-xs">Text Size</Label>
                            <Slider
                              value={[styleOptions.contentFontSize]}
                              onValueChange={(value) => updateStyle('contentFontSize', value[0])}
                              max={24}
                              min={12}
                              step={1}
                              className="mt-1"
                            />
                            <span className="text-xs text-muted-foreground">{styleOptions.contentFontSize}px</span>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </TabsContent>

                <TabsContent value="layout" className="space-y-4">
                  <div className="p-4 border rounded-lg bg-muted/20">
                    <h3 className="font-semibold mb-4">Layout Options</h3>
                    
                    <div className="space-y-4">
                      <div>
                        <Label className="text-sm font-medium">QR Code Position</Label>
                        <Select value={styleOptions.qrPosition} onValueChange={(value) => updateStyle('qrPosition', value)}>
                          <SelectTrigger className="mt-2">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            {qrPositions.map(position => (
                              <SelectItem key={position.value} value={position.value}>{position.name}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>

                      <div>
                        <Label className="text-sm font-medium">QR Code Size</Label>
                        <Slider
                          value={[styleOptions.qrSize]}
                          onValueChange={(value) => updateStyle('qrSize', value[0])}
                          max={200}
                          min={64}
                          step={8}
                          className="mt-2"
                        />
                        <span className="text-xs text-muted-foreground">{styleOptions.qrSize}px</span>
                      </div>

                      <div>
                        <Label className="text-sm font-medium">QR Code Horizontal Position</Label>
                        <Slider
                          value={[styleOptions.qrX]}
                          onValueChange={(value) => updateStyle('qrX', value[0])}
                          max={100}
                          min={0}
                          step={1}
                          className="mt-2"
                        />
                        <span className="text-xs text-muted-foreground">{styleOptions.qrX}% from left</span>
                      </div>

                      <div>
                        <Label className="text-sm font-medium">QR Code Vertical Position</Label>
                        <Slider
                          value={[styleOptions.qrY]}
                          onValueChange={(value) => updateStyle('qrY', value[0])}
                          max={100}
                          min={0}
                          step={1}
                          className="mt-2"
                        />
                        <span className="text-xs text-muted-foreground">{styleOptions.qrY}% from top</span>
                      </div>

                      <div>
                        <Label className="text-sm font-medium">Title Alignment</Label>
                        <Select value={styleOptions.titleAlign} onValueChange={(value) => updateStyle('titleAlign', value)}>
                          <SelectTrigger className="mt-2">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="text-left">Left</SelectItem>
                            <SelectItem value="text-center">Center</SelectItem>
                            <SelectItem value="text-right">Right</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                  </div>
                </TabsContent>
              </Tabs>

              <div className="flex gap-2">
                <Button onClick={exportImage} className="flex-1">
                  <Save className="h-4 w-4 mr-2" />
                  Save
                </Button>
                <Button onClick={downloadImage} variant="outline" className="flex-1">
                  <Download className="h-4 w-4 mr-2" />
                  Download
                </Button>
              </div>
            </div>

            {/* Preview Panel */}
            <div className="space-y-4">
              <div className="p-4 border rounded-lg bg-muted/20">
                <h3 className="font-semibold mb-4">Live Preview</h3>
                
                <div 
                  ref={previewRef}
                  className="relative w-full aspect-[4/3] border rounded-lg overflow-hidden bg-white shadow-lg"
                  style={{
                    backgroundImage: backgroundImageUrl ? `url(${backgroundImageUrl})` : undefined,
                    backgroundSize: 'cover',
                    backgroundPosition: 'center',
                    backgroundRepeat: 'no-repeat'
                  }}
                >
                  {/* Content Overlay */}
                  <div className="absolute inset-0 bg-gradient-to-b from-black/20 via-transparent to-black/30 p-8 flex flex-col justify-between">
                    {/* Header */}
                    <div className={styleOptions.titleAlign}>
                      <h1 
                        className={`${styleOptions.titleFont} mb-2 ${styleOptions.textShadow ? 'drop-shadow-lg' : ''}`}
                        style={{
                          fontSize: `${styleOptions.titleFontSize}px`,
                          color: styleOptions.titleColor,
                          lineHeight: '1.2'
                        }}
                      >
                        {editableData.name || 'Event Name'}
                      </h1>
                      {editableData.hostName && (
                        <p 
                          className={`${styleOptions.textShadow ? 'drop-shadow' : ''}`}
                          style={{
                            fontSize: `${styleOptions.hostFontSize}px`,
                            color: styleOptions.hostColor,
                            opacity: 0.9
                          }}
                        >
                          Hosted by {editableData.hostName}
                        </p>
                      )}
                    </div>

                    {/* Middle Content */}
                    <div 
                      className="backdrop-blur-sm rounded-lg p-6 space-y-3"
                      style={{
                        backgroundColor: `rgba(255, 255, 255, ${styleOptions.contentBgOpacity / 100})`
                      }}
                    >
                      {editableData.description && (
                        <p 
                          className="leading-relaxed"
                          style={{
                            fontSize: `${styleOptions.contentFontSize}px`,
                            color: styleOptions.contentColor
                          }}
                        >
                          {editableData.description}
                        </p>
                      )}
                      
                      <div className="space-y-2">
                        {editableData.date && (
                          <div className="flex items-center gap-2">
                            <span 
                              className="font-semibold"
                              style={{ 
                                fontSize: `${styleOptions.contentFontSize - 2}px`,
                                color: styleOptions.contentColor
                              }}
                            >
                              📅 Date:
                            </span>
                            <span 
                              style={{ 
                                fontSize: `${styleOptions.contentFontSize - 2}px`,
                                color: styleOptions.contentColor
                              }}
                            >
                              {editableData.date}
                            </span>
                          </div>
                        )}
                        
                        {editableData.time && (
                          <div className="flex items-center gap-2">
                            <span 
                              className="font-semibold"
                              style={{ 
                                fontSize: `${styleOptions.contentFontSize - 2}px`,
                                color: styleOptions.contentColor
                              }}
                            >
                              🕐 Time:
                            </span>
                            <span 
                              style={{ 
                                fontSize: `${styleOptions.contentFontSize - 2}px`,
                                color: styleOptions.contentColor
                              }}
                            >
                              {editableData.time}
                            </span>
                          </div>
                        )}
                        
                        {editableData.location && (
                          <div className="flex items-center gap-2">
                            <span 
                              className="font-semibold"
                              style={{ 
                                fontSize: `${styleOptions.contentFontSize - 2}px`,
                                color: styleOptions.contentColor
                              }}
                            >
                              📍 Location:
                            </span>
                            <span 
                              style={{ 
                                fontSize: `${styleOptions.contentFontSize - 2}px`,
                                color: styleOptions.contentColor
                              }}
                            >
                              {editableData.location}
                            </span>
                          </div>
                        )}
                      </div>
                    </div>

                    {/* QR Code */}
                    {qrCodeData && (
                      <div 
                        className="absolute bg-white p-2 rounded-lg shadow-lg animate-fade-in"
                        style={{
                          left: `${styleOptions.qrX}%`,
                          top: `${styleOptions.qrY}%`,
                          transform: 'translate(-50%, -50%)'
                        }}
                      >
                        <img 
                          src={`https://api.qrserver.com/v1/create-qr-code/?size=${styleOptions.qrSize}x${styleOptions.qrSize}&data=${encodeURIComponent(qrCodeData)}`}
                          alt="QR Code"
                          style={{
                            width: `${styleOptions.qrSize}px`,
                            height: `${styleOptions.qrSize}px`
                          }}
                        />
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};